import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Card,
  Spinner,
  Badge,
  Row,
  Col,
  Alert,
  ProgressBar
} from "react-bootstrap";
import {
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Building,
  Shield,
  Check2Square,
  Square
} from 'react-bootstrap-icons';
import { toast } from "react-toastify";

// Import your existing components and APIs
import {
  findSchoolByRollNumber,
  getSchoolScreeners,
  upsertSchoolScreener
} from "../services/APIClient";

const AddSchoolScreenerModal = ({
  show,
  onHide,
  availableScreeningPoints = [],
  onSchoolAdded,
  currentUser = "admin", // You'll want to pass the actual current user
  editMode = false,
  initialSchool = null // For edit mode
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(editMode ? 2 : 1);
  const totalSteps = editMode ? 3 : 4; // Skip school lookup in edit mode

  // Data states
  const [rollNumber, setRollNumber] = useState("");
  const [selectedSchool, setSelectedSchool] = useState(editMode ? initialSchool : null);
  const [existingScreeners, setExistingScreeners] = useState([]);
  const [selectedScreeners, setSelectedScreeners] = useState(new Set());
  const [windowDates, setWindowDates] = useState({});

  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // In edit mode, skip straight to screener selection for the given school
  useEffect(() => {
    if (show && editMode && initialSchool) {
      setSelectedSchool(initialSchool);
      setCurrentStep(2);
      loadExistingScreeners(initialSchool.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editMode, initialSchool]);

  // Reset modal state
  const resetModal = () => {
    setCurrentStep(editMode ? 2 : 1);
    setRollNumber("");
    setSelectedSchool(editMode ? initialSchool : null);
    setExistingScreeners([]);
    setSelectedScreeners(new Set());
    setWindowDates({});
    setError(null);
  };

  const handleClose = () => {
    resetModal();
    onHide();
  };

  // Step 1: School lookup
  const handleSchoolLookup = async () => {
    if (!rollNumber.trim()) {
      setError("Please enter a valid roll number.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const school = await findSchoolByRollNumber(rollNumber.trim());

      if (school && school.id) {
        setSelectedSchool(school);
        await loadExistingScreeners(school.id);
        setCurrentStep(2);
        toast.success("School found successfully!");
      } else {
        setError("No school found with this roll number.");
      }
    } catch (err) {
      console.error("Error fetching school:", err);
      setError("Error fetching the school. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load existing screeners for the school
  const loadExistingScreeners = async (schoolId) => {
    try {
      const screeners = await getSchoolScreeners(schoolId);
      setExistingScreeners(screeners);

      // Pre-select existing active screeners
      const activeScreenerIds = screeners
        .filter(s => s.active)
        .map(s => s.assessmentid);
      setSelectedScreeners(new Set(activeScreenerIds));
      setWindowDates(Object.fromEntries(screeners.map(screener => [
        screener.assessmentid,
        {
          opening: toDateInputValue(screener.dateopened),
          closing: toDateInputValue(screener.dateclosed),
        },
      ])));
    } catch (err) {
      console.error("Error loading existing screeners:", err);
      // Continue without existing data - not critical
      setExistingScreeners([]);
    }
  };

  // Step 2: Screener selection
  const handleScreenerToggle = (screenerId) => {
    const newSelected = new Set(selectedScreeners);
    if (newSelected.has(screenerId)) {
      newSelected.delete(screenerId);
    } else {
      newSelected.add(screenerId);
    }
    setSelectedScreeners(newSelected);
  };

  const handleSelectAllByCategory = (category) => {
    const categoryScreeners = availableScreeningPoints
      .filter(point => point.category === category)
      .map(point => point.id);

    const newSelected = new Set(selectedScreeners);
    const allSelected = categoryScreeners.every(id => newSelected.has(id));

    if (allSelected) {
      // Deselect all in category
      categoryScreeners.forEach(id => newSelected.delete(id));
    } else {
      // Select all in category
      categoryScreeners.forEach(id => newSelected.add(id));
    }

    setSelectedScreeners(newSelected);
  };

  const handleWindowDateChange = (screenerId, field, value) => {
    setWindowDates(previous => ({
      ...previous,
      [screenerId]: {...previous[screenerId], [field]: value},
    }));
  };

  const validateSelectedWindows = () => {
    for (const assessmentId of selectedScreeners) {
      const dates = windowDates[assessmentId] || {};
      if (!dates.opening || !dates.closing) {
        return "Every selected screener needs an opening and closing date.";
      }
      if (dates.closing < dates.opening) {
        return "A screener closing date cannot be before its opening date.";
      }
    }
    return null;
  };

  const moveToReview = () => {
    const validationError = validateSelectedWindows();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setCurrentStep(3);
  };

  // Step 3: Review and save
  const handleSaveScreeners = async () => {
    if (!selectedSchool) {
      setError("No school selected.");
      return;
    }

    const validationError = validateSelectedWindows();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updatePromises = [];
      const allScreenerIds = availableScreeningPoints.map(point => point.id);

      // Process each available screener
      for (const assessmentId of allScreenerIds) {
        const shouldBeActive = selectedScreeners.has(assessmentId);
        const existingScreener = existingScreeners.find(s => s.assessmentid === assessmentId);
        if (!shouldBeActive && !existingScreener?.active) continue;

        const screenerData = {
          active: shouldBeActive,
          created_by: existingScreener?.created_by || currentUser,
          updated_by: currentUser,
        };

        if (shouldBeActive) {
          const dates = windowDates[assessmentId];
          screenerData.dateopened = `${dates.opening}T00:00:00.000Z`;
          screenerData.dateclosed = `${dates.closing}T23:59:59.999Z`;
        } else {
          if (existingScreener?.dateopened) screenerData.dateopened = existingScreener.dateopened;
          if (existingScreener?.dateclosed) screenerData.dateclosed = existingScreener.dateclosed;
        }

        updatePromises.push(
          upsertSchoolScreener(selectedSchool.id, assessmentId, screenerData)
        );
      }

      // Execute all updates
      if (updatePromises.length > 0) {
        const results = await Promise.allSettled(updatePromises);

        // Check for any failures
        const failures = results.filter(result => result.status === 'rejected');
        if (failures.length > 0) {
          console.error('Some updates failed:', failures);
          throw new Error(`${failures.length} out of ${updatePromises.length} updates failed`);
        }
      }

      setCurrentStep(editMode ? 3 : 4);
      const actionText = editMode ? 'updated' : 'configured';
      toast.success(`Successfully ${actionText} screeners for ${selectedSchool.name}`);

      // Notify parent component
      if (onSchoolAdded) {
        onSchoolAdded(selectedSchool, selectedScreeners);
      }

    } catch (err) {
      console.error("Error saving screeners:", err);
      setError(`Failed to save screener configuration: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Group screeners by category for better organization
  const groupedScreeners = availableScreeningPoints.reduce((groups, screener) => {
    const category = screener.category || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(screener);
    return groups;
  }, {});

  const getStepTitle = () => {
    if (editMode) {
      switch(currentStep) {
        case 2: return "Edit Screeners";
        case 3: return "Complete";
        default: return "Edit School Screeners";
      }
    }
    switch(currentStep) {
      case 1: return "Find School";
      case 2: return "Select Screeners";
      case 3: return "Review & Save";
      case 4: return "Complete";
      default: return "Add School Screeners";
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <Building className="me-2" />
          {getStepTitle()}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">Step {currentStep} of {totalSteps}</small>
            <small className="text-muted">{Math.round((currentStep / totalSteps) * 100)}% Complete</small>
          </div>
          <ProgressBar
            now={(currentStep / totalSteps) * 100}
            variant="primary"
            style={{ height: '4px' }}
          />
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: School Lookup (Create mode only) */}
        {currentStep === 1 && !editMode && (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <div className="school-icon mx-auto mb-3">
                  <Building size={32} />
                </div>
                <h5 className="mb-2">Find School</h5>
                <p className="text-muted">Enter the school's roll number or unique identifier</p>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">School Identifier</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter roll number or URN"
                  value={rollNumber}
                  onChange={(e) => setRollNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSchoolLookup()}
                  disabled={loading}
                  size="lg"
                  style={{ borderRadius: '8px' }}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        )}

        {/* Step 2: Screener Selection */}
        {currentStep === 2 && selectedSchool && (
          <>
            {/* School Info */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <Building className="me-3 text-primary" size={24} />
                  <div>
                    <h6 className="mb-0">{selectedSchool.name}</h6>
                    <small className="text-muted">ID: {selectedSchool.id}</small>
                  </div>
                  <Badge bg="success" className="ms-auto">
                    <CheckCircle className="me-1" />
                    {editMode ? 'Editing' : 'Found'}
                  </Badge>
                </div>
              </Card.Body>
            </Card>

            {/* Screener Selection */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">
                    {editMode ? 'Edit Active Screeners' : 'Select Screeners to Activate'}
                  </span>
                  <Badge bg="info">{selectedScreeners.size} selected</Badge>
                </div>
              </Card.Header>
              <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {Object.entries(groupedScreeners).map(([category, screeners]) => (
                  <div key={category} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 text-primary">{category}</h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleSelectAllByCategory(category)}
                      >
                        {screeners.every(s => selectedScreeners.has(s.id)) ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>

                    {screeners.map(screener => {
                      const isSelected = selectedScreeners.has(screener.id);
                      const isExisting = existingScreeners.some(
                        e => e.assessmentid === screener.id && e.active
                      );

                      return (
                        <div
                          key={screener.id}
                          className={`p-3 border rounded mb-2 cursor-pointer ${
                            isSelected ? 'border-primary bg-primary bg-opacity-10' : 'border-light'
                          }`}
                          onClick={() => handleScreenerToggle(screener.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              {isSelected ? (
                                <Check2Square className="text-primary" size={20} />
                              ) : (
                                <Square className="text-muted" size={20} />
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center">
                                <strong>{screener.name}</strong>
                                {isExisting && (
                                  <Badge bg="warning" className="ms-2">Currently Active</Badge>
                                )}
                              </div>
                              {screener.description && (
                                <small className="text-muted d-block">{screener.description}</small>
                              )}
                              <div className="mt-1">
                                <Badge bg={screener.isGrade2 ? "info" : "primary"} className="me-1">
                                  {screener.gradeLevel}
                                </Badge>
                                <Badge bg={screener.isCustom ? "warning" : "success"}>
                                  {screener.isCustom ? "Custom" : "Standard"}
                                </Badge>
                              </div>
                              {isSelected && (
                                <Row
                                  className="mt-3"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <Col md={6}>
                                    <Form.Group controlId={`opening-${screener.id}`}>
                                      <Form.Label>Opening date</Form.Label>
                                      <Form.Control
                                        type="date"
                                        value={windowDates[screener.id]?.opening || ""}
                                        onChange={(event) => handleWindowDateChange(
                                          screener.id,
                                          "opening",
                                          event.target.value
                                        )}
                                        required
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Group controlId={`closing-${screener.id}`}>
                                      <Form.Label>Closing date</Form.Label>
                                      <Form.Control
                                        type="date"
                                        min={windowDates[screener.id]?.opening || undefined}
                                        value={windowDates[screener.id]?.closing || ""}
                                        onChange={(event) => handleWindowDateChange(
                                          screener.id,
                                          "closing",
                                          event.target.value
                                        )}
                                        required
                                      />
                                    </Form.Group>
                                  </Col>
                                </Row>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </Card.Body>
            </Card>
          </>
        )}

        {/* Step 3: Review (Create mode) */}
        {currentStep === 3 && !editMode && (
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h6 className="mb-0">Review Configuration</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>School:</strong>
                    <div>{selectedSchool?.name}</div>
                    <small className="text-muted">ID: {selectedSchool?.id}</small>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Screeners to Activate:</strong>
                    <div>{selectedScreeners.size} selected</div>
                  </div>
                </Col>
              </Row>

              <div className="mt-3">
                <strong>Selected Screeners:</strong>
                <div className="mt-2">
                  {Array.from(selectedScreeners).map(screenerId => {
                    const screener = availableScreeningPoints.find(s => s.id === screenerId);
                    return screener ? (
                      <div key={screenerId} className="mb-2">
                        <Badge bg="primary" className="me-2">{screener.name}</Badge>
                        <small className="text-muted">
                          {windowDates[screenerId]?.opening} to {windowDates[screenerId]?.closing}
                        </small>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Step 4: Success (Create mode) or Step 3: Success (Edit mode) */}
        {((currentStep === 4 && !editMode) || (currentStep === 3 && editMode)) && (
          <div className="text-center py-4">
            <CheckCircle className="text-success mb-3" size={48} />
            <h5 className="text-success mb-3">Configuration Complete!</h5>
            <p className="text-muted mb-4">
              Successfully {editMode ? 'updated' : 'configured'} screeners for {selectedSchool?.name}
            </p>
            <Alert variant="success">
              The selected screeners are now {editMode ? 'updated' : 'active'} for this school and can be used immediately.
            </Alert>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div>
          {((currentStep > 1 && currentStep < 4 && !editMode) || (currentStep === 2 && editMode)) && (
            <Button
              variant="outline-secondary"
              onClick={() => setCurrentStep(currentStep - 1)}
              disabled={loading}
            >
              <ArrowLeft className="me-1" />
              Back
            </Button>
          )}
        </div>

        <div>
          {currentStep === 1 && !editMode && (
            <Button
              variant="primary"
              onClick={handleSchoolLookup}
              disabled={loading || !rollNumber.trim()}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Searching...
                </>
              ) : (
                <>
                  Find School
                  <ArrowRight className="ms-1" />
                </>
              )}
            </Button>
          )}

          {currentStep === 2 && (
            <Button
              variant="primary"
              onClick={editMode ? handleSaveScreeners : moveToReview}
              disabled={selectedScreeners.size === 0 || loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Saving...
                </>
              ) : (
                <>
                  {editMode ? 'Save Changes' : 'Review Selection'}
                  <ArrowRight className="ms-1" />
                </>
              )}
            </Button>
          )}

          {currentStep === 3 && !editMode && (
            <Button
              variant="success"
              onClick={handleSaveScreeners}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Saving...
                </>
              ) : (
                <>
                  <Shield className="me-1" />
                  Activate Screeners
                </>
              )}
            </Button>
          )}

          {((currentStep === 4 && !editMode) || (currentStep === 3 && editMode)) && (
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          )}
        </div>
      </Modal.Footer>

      <style>{`
        .school-icon {
          width: 64px;
          height: 64px;
          background: rgba(13, 110, 253, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0d6efd;
        }
      `}</style>
    </Modal>
  );
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

export default AddSchoolScreenerModal;
